// src/features/brands/brandSlice.ts
import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { fetchBrands as fetchBrandsAPI } from "../../services/brand";
import { ExtendedBrand, FetchResult } from "../../pages/PartnerBrandTab/BrandTypes";

interface BrandState {
  brands: ExtendedBrand[];
  total: number;
  loading: boolean;
  page: number;
  pageSize: number;
  searchQuery: string;
  sortBy: string;
  sortOrder: "asc" | "desc";
  hasFetched: boolean;
  error?: string;
}

const initialState: BrandState = {
  brands: [],
  total: 0,
  loading: false,
  page: 1,
  pageSize: 10,
  searchQuery: "",
  sortBy: "_id",
  sortOrder: "desc",
  hasFetched: false,
};

// --- Async thunk without generics ---
export const fetchBrandsThunk = createAsyncThunk(
  "partnerBrand/fetchBrands",
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState() as { partnerBrand: BrandState; auth: { token: string } };
      const { page, pageSize, searchQuery, sortBy, sortOrder } = state.partnerBrand;
      const { token } = state.auth;

      return await fetchBrandsAPI({
        page,
        pageSize,
        sortField: sortBy,
        sortOrder,
        search: searchQuery,
        token,
      });
    } catch (err: any) {
      return rejectWithValue(err.message);
    }
  }
);

const partnerBrandSlice = createSlice({
  name: "partnerBrand",
  initialState,
  reducers: {
    setPage: (state, action: PayloadAction<number>) => {
      state.page = action.payload;
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload;
    },
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload;
    },
    setSortBy: (state, action: PayloadAction<string>) => {
      state.sortBy = action.payload;
    },
    setSortOrder: (state, action: PayloadAction<"asc" | "desc">) => {
      state.sortOrder = action.payload;
    },
    resetBrandsState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBrandsThunk.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(fetchBrandsThunk.fulfilled, (state, action) => {
        const result: FetchResult = action.payload;

        state.loading = false;
        state.brands = result.Brands.map((b) => ({
          ...b,
          groupId: b.groupId || "",
          token: b.token || "",
          isEv: b.isEv || false,
          lender: b.lender || "",
        })) as ExtendedBrand[];

        state.total = result.totalMatch;
        state.hasFetched = true;
      })
      .addCase(fetchBrandsThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  setPage,
  setPageSize,
  setSearchQuery,
  setSortBy,
  setSortOrder,
  resetBrandsState,
} = partnerBrandSlice.actions;

export default partnerBrandSlice.reducer;
